import React, { useLayoutEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import {
    IconButton,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import ReportsService from '../../../Services/reports.service';

// Column order matches BuildVatTransactionDocumentationHtml.
const VAT_DETAIL_COLUMN_TYPES = [
    'number', // תנועה
    'string', // חן חובה
    'string', // חן זכות
    'date',   // תאריך
    'string', // פרטים
    'string', // אסמכתא
    'number', // סה״כ סכום
    'number', // אחוז מע״מ
    'number', // סכום נטו
    'number', // מ.ע.מ
    'month',  // ח.דיווח
    'string', // קוד מיון
];

const REPORTING_MONTH_COLUMN = 10;

function cellSortValue(row, index, type) {
    const cell = row?.children?.[index];
    if (!cell) return null;
    const text = (cell.textContent || '').replace(/\u00a0/g, ' ').trim();
    if (!text) return null;

    if (type === 'number') {
        const negative = text.startsWith('(') && text.endsWith(')');
        const cleaned = text.replace(/[()\s]/g, '').replace(/,/g, '');
        const value = Number(cleaned);
        if (Number.isNaN(value)) return null;
        return negative ? -value : value;
    }

    if (type === 'date') {
        const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return null;
        return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
    }

    if (type === 'month') {
        const match = text.match(/^(\d{1,2})\/(\d{4})$/);
        if (!match) return null;
        return new Date(Number(match[2]), Number(match[1]) - 1, 1).getTime();
    }

    return text;
}

function compareSortValues(a, b, type, direction) {
    const aEmpty = a == null || a === '';
    const bEmpty = b == null || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    let result = 0;
    if (type === 'number' || type === 'date' || type === 'month') {
        result = a - b;
    } else {
        result = String(a).localeCompare(String(b), 'he', { numeric: true, sensitivity: 'base' });
    }
    return direction === 'desc' ? -result : result;
}

function sectionMonthValue(section) {
    const fromRow = cellSortValue(section.rows[0], REPORTING_MONTH_COLUMN, 'month');
    if (fromRow != null) return fromRow;
    const headerText = section.header?.textContent || '';
    const match = headerText.match(/(\d{1,2})\/(\d{4})/);
    if (!match) return null;
    return new Date(Number(match[2]), Number(match[1]) - 1, 1).getTime();
}

function sortVatDetailsTable(table, columnIndex, direction) {
    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const type = VAT_DETAIL_COLUMN_TYPES[columnIndex] || 'string';
    const children = Array.from(tbody.children);
    const monthFooters = children.filter((row) => row.classList.contains('tr-final-row'));
    const grandTotal = monthFooters[monthFooters.length - 1] || null;

    const sections = [];
    let current = null;

    children.forEach((row) => {
        if (row === grandTotal) return;
        if (row.classList.contains('month-header')) {
            current = { header: row, rows: [], footer: null };
            sections.push(current);
            return;
        }
        if (row.classList.contains('tr-final-row')) {
            if (current) current.footer = row;
            return;
        }
        if (row.classList.contains('tr') && row.hasAttribute('data-id')) {
            if (!current) {
                current = { header: null, rows: [], footer: null };
                sections.push(current);
            }
            current.rows.push(row);
        }
    });

    if (columnIndex === REPORTING_MONTH_COLUMN) {
        sections.sort((a, b) => compareSortValues(
            sectionMonthValue(a),
            sectionMonthValue(b),
            'month',
            direction
        ));
    }

    sections.forEach((section) => {
        section.rows.sort((a, b) => compareSortValues(
            cellSortValue(a, columnIndex, type),
            cellSortValue(b, columnIndex, type),
            type,
            direction
        ));
    });

    const fragment = document.createDocumentFragment();
    sections.forEach((section) => {
        if (section.header) fragment.appendChild(section.header);
        section.rows.forEach((row) => fragment.appendChild(row));
        if (section.footer) fragment.appendChild(section.footer);
    });
    if (grandTotal) fragment.appendChild(grandTotal);
    tbody.appendChild(fragment);
}

function applyVatDetailsSort(root, sort) {
    const table = root.querySelector('table.table');
    if (!table) return;

    const headers = table.querySelectorAll('thead th');
    headers.forEach((header, index) => {
        const label = (header.textContent || '').trim();
        const active = sort?.index === index;
        header.classList.toggle('sorted', active);
        header.title = 'מיון';
        header.replaceChildren();
        if (active) {
            const indicator = document.createElement('span');
            indicator.className = 'sort-ind';
            indicator.textContent = sort.direction === 'desc' ? '▼' : '▲';
            header.appendChild(indicator);
        }
        header.appendChild(document.createTextNode(label));
    });

    if (!sort) return;

    const hint = root.querySelector('.det-small');
    const label = headers[sort.index]?.textContent?.replace(/[▲▼]/g, '').trim();
    if (hint && label) {
        const directionLabel = sort.direction === 'desc' ? 'יורד' : 'עולה';
        hint.textContent = `ממוין לפי ${label} (${directionLabel})`;
    }

    sortVatDetailsTable(table, sort.index, sort.direction);
}

const HtmlViewerModal = ({
    open = false,
    title,
    onClose = () => { },
    htmlContent = '',
    onRowClick = () => { },
    vatRequestDto,
    token
}) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);
    const contentRef = useRef(null);
    const [sortState, setSortState] = useState(null);
    const sort = sortState?.key === htmlContent ? sortState : null;

    useLayoutEffect(() => {
        const root = contentRef.current;
        if (!root) return;
        root.innerHTML = htmlContent || '';
        if (htmlContent) applyVatDetailsSort(root, sort);
    }, [htmlContent, sort, open]);

    if (!open) return null;

    const handleRowClick = (e) => {
        const tr = e.target.closest('tr.tr');
        if (!tr || !tr.getAttribute('data-id')) return;
        const cells = Array.from(tr.querySelectorAll('td')).map((cell) => cell.textContent);
        const id = tr.getAttribute('data-id');
        const type = tr.getAttribute('data-type');
        onRowClick({ cells, id, type });
    };

    const handleContentClick = (e) => {
        const header = e.target.closest('thead th');
        if (header) {
            const index = header.cellIndex;
            setSortState((prev) => {
                const current = prev?.key === htmlContent ? prev : null;
                if (current?.index === index) {
                    return {
                        key: htmlContent,
                        index,
                        direction: current.direction === 'asc' ? 'desc' : 'asc',
                    };
                }
                return { key: htmlContent, index, direction: 'asc' };
            });
            return;
        }
        handleRowClick(e);
    };

    const handleDownloadClick = async () => {
        try {
            await ReportsService.vatTransactionPdf(vatRequestDto, token);
        } catch (err) {
            console.error("Failed to download VAT PDF:", err);
        }
    };

    return (
        <Draggable handle=".drag-handle" nodeRef={nodeRef}>
            <div
                ref={nodeRef}
                className="vat-html-viewer"
                style={{
                    position: 'absolute',
                    top: '15%',
                    left: '30%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    minWidth: '900px',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    zIndex: 1300,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
                    border: '1px solid #304FFF'
                }}
            >
                <style>{`
                    .vat-html-viewer table.table thead th {
                        cursor: pointer;
                        user-select: none;
                        position: sticky;
                        top: 0;
                        background: #fff;
                        z-index: 2;
                    }
                    .vat-html-viewer table.table thead th:hover {
                        background: #f3f5ff;
                    }
                    .vat-html-viewer table.table thead th.sorted {
                        color: #304FFF;
                    }
                    .vat-html-viewer .sort-ind {
                        font-size: 10px;
                        margin-inline-end: 4px;
                    }
                `}</style>
                {/* Modal Header */}
                <div
                    className="drag-handle"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'move',
                        marginBottom: 8
                    }}
                >
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Tooltip title={t('Close')}>
                            <IconButton onClick={onClose}>
                                <CancelIcon style={{ color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('DownloadPDFFile')}>
                            <IconButton onClick={handleDownloadClick}>
                                <DownloadIcon style={{ color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        {title}
                    </div>
                </div>

                {/* HTML Content */}
                <div
                    ref={contentRef}
                    onClick={handleContentClick}
                    style={{
                        maxHeight: '65vh',
                        overflowY: 'auto',
                        cursor: 'pointer',
                    }}
                />
            </div>
        </Draggable>
    );
};

export default HtmlViewerModal;
